var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { C } from '#integrations';
import { Button, IconButton } from '../buttons';
import { ChevronDownIcon, ChevronUpIcon } from '../icons';
import { useAccordionGroup } from './context';
import { AccordionStates, AccordionTestIdFactory } from './interactions';
import styles from './Accordion.module.css';
export const Accordion = ({ as, buttonText, children, 'data-testid': testid, disabled = false, id, isExpanded = false, onToggle, panelClassName, renderHeader, shouldExpandOnHeaderClick = false, shouldRemoveContentOnCollapse = false, }) => {
    _react_refresh_temp_2();
    // eslint-disable-next-line @typescript-eslint/naming-convention -- representing a Component
    const Heading = as;
    const headingId = `${id}-heading`;
    const panelId = `${id}-panel`;
    const { accordionGroupData: { expandedId: expandedIdFromContext, isGrouped }, setAccordionGroupExpandedId, } = useAccordionGroup();
    const [isCurrentlyExpanded, setIsCurrentlyExpanded] = useState(isExpanded);
    const [isVisibleContent, setIsVisibleContent] = useState(isExpanded);
    useEffect(() => {
        if (isGrouped && isExpanded) {
            setAccordionGroupExpandedId(id);
        }
        if (!isGrouped) {
            setIsCurrentlyExpanded(isExpanded);
        }
    }, [id, isExpanded, isGrouped, setAccordionGroupExpandedId]);
    useEffect(() => {
        if (isGrouped) {
            setIsCurrentlyExpanded(expandedIdFromContext === id);
        }
    }, [expandedIdFromContext, id, isGrouped]);
    useEffect(() => {
        if (isCurrentlyExpanded) {
            setIsVisibleContent(true);
        }
    }, [isCurrentlyExpanded]);
    const handleToggle = useCallback(() => {
        if (onToggle) {
            onToggle(!isCurrentlyExpanded);
        }
        else {
            setAccordionGroupExpandedId(id);
            if (!isGrouped) {
                setIsCurrentlyExpanded((prev) => !prev);
            }
        }
    }, [
        onToggle,
        isCurrentlyExpanded,
        setAccordionGroupExpandedId,
        id,
        isGrouped,
    ]);
    const onHeaderClick = useCallback(() => {
        if (disabled) {
            return;
        }
        if (shouldExpandOnHeaderClick) {
            handleToggle();
        }
    }, [disabled, handleToggle, shouldExpandOnHeaderClick]);
    const onToggleButtonClick = useCallback((event) => {
        if (disabled) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        handleToggle();
    }, [disabled, handleToggle]);
    const commonToggleButtonProps = useMemo(() => {
        return {
            'aria-controls': panelId,
            'aria-expanded': isCurrentlyExpanded,
            'data-testid': testid,
            kind: 'ghost',
            disabled,
            onClick: onToggleButtonClick,
            size: 'small',
        };
    }, [disabled, isCurrentlyExpanded, onToggleButtonClick, panelId, testid]);
    const ToggleButtonIcon = useMemo(() => {
        return isCurrentlyExpanded ? ChevronUpIcon : ChevronDownIcon;
    }, [isCurrentlyExpanded]);
    return (_jsxs("div", { className: styles.Container, "data-test-state": disabled ? AccordionStates.Disabled : AccordionStates.Active, "data-testid": AccordionTestIdFactory.getAccordionTestId(testid), children: [_jsx(Heading, { "data-testid": AccordionTestIdFactory.getAccordionHeaderTestId(testid), onClick: onHeaderClick, children: _jsx("div", { className: C(styles.HeaderContainer, disabled && styles.HeaderContainerDisabled, !disabled && shouldExpandOnHeaderClick
                        ? styles.HeaderClickable
                        : undefined), id: headingId, children: renderHeader({
                        ToggleButton: buttonText ? (_jsx(Button, { ...commonToggleButtonProps, EndIcon: ToggleButtonIcon, children: buttonText })) : (_jsx(IconButton, { ...commonToggleButtonProps, "aria-labelledby": headingId, Icon: ToggleButtonIcon })),
                        isExpanded: isCurrentlyExpanded,
                        setIsCurrentlyExpanded,
                    }) }) }), _jsx("div", { "aria-labelledby": headingId, className: C(styles.Panel, isCurrentlyExpanded
                    ? styles.PanelExpanded
                    : styles.PanelCollapsed, panelClassName), "data-testid": AccordionTestIdFactory.getAccordionPanelTestId(testid), id: panelId, onTransitionEnd: (e) => {
                    // Delay the element removal from DOM.
                    // The if is here to set the value only when
                    // the result of animation is a collapsed container.
                    // This marks the end of closing animation.
                    if (e.currentTarget.clientHeight === 0) {
                        setIsVisibleContent(false);
                    }
                }, role: "region", children: _jsx("div", { className: styles.PanelContent, children: isVisibleContent || !shouldRemoveContentOnCollapse ? (_jsx(_Fragment, { children: children })) : null }) })] }));
};
_react_refresh_temp_1 = Accordion;
$RefreshReg$(_react_refresh_temp_1, "Accordion");
_react_refresh_temp_2(Accordion, "dKKs4Amdw1t4xzEO0jDEN1v80Ow=", false, () => [useAccordionGroup]);
